<template>
  <div class="min-h-screen bg-[#f9fafb] px-4 pb-20 pt-5">
    <!-- Header -->
    <div class="mb-6">
      <div
        class="flex items-center justify-between rounded-[22px] p-5 bg-white shadow-sm"
      >
        <!-- Kiri: avatar + greeting + nama -->
        <div class="flex items-center gap-3">
          <div class="flex items-center gap-4">
            <!-- avatar dalam lingkaran hijau (pakai fallback) -->
            <div
              class="w-12 h-12 rounded-full bg-[#E6F6E9] grid place-items-center"
            >
              <img
                :src="avatarSrc"
                class="w-9 h-9 rounded-full object-cover"
                alt="avatar"
                @error="onAvatarError"
              >
            </div>

            <div class="leading-tight">
              <p class="text-xs text-[#9AA3AF]">
                {{ t("dashboard.greeting") }}
              </p>
              <p class="text-lg font-normal text-[#1C103B]">
                {{ profile.name || "-" }}
              </p>
            </div>
          </div>

          <!-- Kanan: pill XP + bell -->
          <div class="flex items-center gap-3">
            <!-- XP pill -->
            <div
              class="flex items-center gap-2 bg-[#F0ECFE] px-4 py-2 rounded-full"
            >
              <!-- diamond icon -->
              <svg
                viewBox="0 0 24 24"
                class="w-5 h-5 text-[#7C5CFF]"
                fill="currentColor"
                aria-hidden="true"
              >
                <path
                  d="M7.5 3.5h9l4 5-9 12-9-12 4-5zM9 5.5l-2.5 3L12 18l5.5-9.5-2.5-3H9z"
                />
              </svg>
              <span class="text-base font-semibold text-[#7C5CFF]">
                {{ availableXp }}
              </span>
            </div>
          </div>
        </div>

        <!-- bell (route ke /notification) -->
        <button
          class="text-[#1C103B]"
          :aria-label="t('dashboard.notificationsAria')"
          @click="goToUrl('/notification')"
        >
          <i class="far fa-bell text-xl" />
        </button>
      </div>
    </div>

    <!-- Congratulations Card -->
    <div class="bg-white rounded-xl p-4 border relative overflow-hidden mb-6">
      <div>
        <p class="text-base font-semibold text-[#1E1E1F] mb-1">
          {{ t("dashboard.congratulations") }}
        </p>
        <p class="text-sm text-gray-500">
          {{ t("dashboard.closeToReward") }}
        </p>
        <div class="flex gap-2 items-center mt-2">
          <p class="text-base text-[#1E1E1F]">
            {{ availableXp }} / {{ xpTarget }}
          </p>
          <span>{{ t("dashboard.xpLabel") }}</span>
        </div>

        <p class="text-[10px] text-gray-400 mb-2">
          {{ xpProgressDisplay }}
        </p>
        <!-- <BaseButton class="rounded-xl" @click="goToUrl('/rewards')">
          View All Rewards
        </BaseButton> -->
      </div>
      <img
        src="@/assets/cup-confetti.png"
        class="absolute right-2 bottom-2 w-42"
      >
    </div>

    <!-- Subjects -->
    <div class="bg-white rounded-2xl shadow-custom p-4 mb-4">
      <div class="flex justify-between items-center mb-4">
        <h3 class="text-base font-semibold text-gray-900">
          {{ t("dashboard.subjectsTitle") }}
        </h3>
        <a
          href="/subject-all"
          class="text-sm text-blue-600 font-medium"
        >{{ t("dashboard.viewAll") }}</a>
      </div>

      <div class="flex justify-between">
        <div
          v-for="subject in subjects"
          :key="subject.SubjectId ?? subject.Id ?? subject.name"
          class="flex flex-col items-center text-center cursor-pointer"
          role="button"
          tabindex="0"
          :aria-label="
            t('dashboard.subjectGoTo', {
              subject: subject.name ?? subject.SubjectName,
            })
          "
          :title="
            t('dashboard.subjectFilter', {
              subject: subject.name ?? subject.SubjectName,
            })
          "
          @click="goToQuizWithSubject(subject)"
          @keydown.enter="goToQuizWithSubject(subject)"
        >
          <img
            :src="subject.icon"
            alt=""
            class="w-[52px] h-[52px] object-contain"
          >
          <p class="text-sm mt-2 text-gray-800">
            {{ subject.name ?? subject.SubjectName }}
          </p>
        </div>
      </div>
    </div>

    <BaseButton
      class="mb-4 py-4 rounded-xl"
      @click="goToUrl('/quiz/generate')"
    >
      <div class="flex gap-3 items-center w-full">
        <img
          src="@/assets/ideas.png"
          class="w-8 h-8 rounded-full"
        >
        <p class="font-semibold text-base text-left">
          {{ t("dashboard.createQuizCta") }}
        </p>
        <i class="fas fa-angle-right w-8" />
      </div>
    </BaseButton>

    <!-- Leaderboard -->
    <div class="bg-white rounded-2xl shadow-custom p-4 mb-6">
      <div class="flex justify-between items-center mb-2">
        <h3 class="font-semibold text-base text-gray-900">
          {{ t("dashboard.leaderboardTitle") }}
        </h3>
        <a
          href="/ranking"
          class="text-sm text-blue-600 font-medium"
        >{{ t("dashboard.viewAll") }}</a>
      </div>
      <div class="space-y-2">
        <div
          v-for="(user, index) in leaderboard"
          :key="index"
          class="flex justify-between items-center bg-white p-3 border-b"
        >
          <div class="flex items-center gap-3">
            <img
              :src="user.avatar"
              class="w-10 h-10 rounded-full"
            >
            <div>
              <p class="text-sm font-medium">
                {{ user.name }}
              </p>
              <p class="text-xs text-gray-400">
                {{ user.xp }} {{ t("dashboard.xpLabel") }}
              </p>
            </div>
          </div>
          <img
            :src="user.badge"
            class="w-9 h-9 rounded-full"
          >
        </div>
      </div>
    </div>

    <!-- Live Sessions -->
    <liveSessionComponent />

    <!-- Bottom Navigation -->
    <BottomBarNavigation />
  </div>
</template>

<script setup>
import { useRouter } from "vue-router";
import BaseButton from "../base/BaseButton.vue";
import BottomBarNavigation from "../base/BottomBarNavigation.vue";
import liveSessionComponent from "../base/LiveSessionComponent.vue";
import { useAuthStore } from "@/stores/authStore";
import { computed, onMounted, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { GetProfile } from "@/api/settingApi";
import { GlobalAllTimeLeaderboard } from "@/api/leaderboardApi";
import { getRewardsByStudentId } from "@/api/rewardApi";
/* default avatar */
import DefaultAvatar from "@/assets/Avatar.png";

const router = useRouter();
const auth = useAuthStore();
const { t } = useI18n();

function goToUrl(dir) {
  router.push(dir);
}

const profile = ref({});
/* avatarSrc + fallback */
const avatarSrc = ref(DefaultAvatar);

function resolveAvatarSrc(filePath) {
  if (!filePath || typeof filePath !== "string") return DefaultAvatar;

  try {
    if (/^https?:\/\//i.test(filePath)) return filePath; // absolut
    const base =
      import.meta.env.VITE_ASSET_BASE_URL ||
      import.meta.env.VITE_API_BASE_URL ||
      "";
    if (!base) return DefaultAvatar;
    const cleanBase = String(base).replace(/\/+$/, "");
    const cleanPath = String(filePath).replace(/^\/+/, "");
    return `${cleanBase}/${cleanPath}`;
  } catch {
    return DefaultAvatar;
  }
}

function onAvatarError(e) {
  const img = e?.target;
  if (img && img.src !== DefaultAvatar) img.src = DefaultAvatar;
}

/* sinkron saat profile berubah */
watchEffect(() => {
  avatarSrc.value = resolveAvatarSrc(profile.value?.filePath);
});

const handleGetProfile = async () => {
  try {
    const userId = auth.userId;
    const response = await GetProfile(userId);
    profile.value = response || {};
    fetchRewardsSummary(response.studentId);
  } catch (error) {
    console.error(error);
    profile.value = {};
  }
};

const goToQuizWithSubject = (subject) => {
  const id = subject?.SubjectId ?? subject?.Id ?? subject?.id ?? null;
  const name = subject?.SubjectName ?? subject?.name ?? "";

  const query = {};
  if (id != null) query.subjectId = String(id);
  if (name) query.subjectName = name;

  router.push({ path: "/quiz", query });
};

const leaderboard = ref([]);

const fetchLeaderboard = async () => {
  try {
    const data = await GlobalAllTimeLeaderboard(4);
    leaderboard.value = data.map((user, index) => ({
      name: user.Name,
      xp: user.XP,
      avatar: require("@/assets/Rectangle.png"),
      badge: getBadge(index + 1),
    }));
  } catch (error) {
    console.error("Error fetching leaderboard:", error);
    leaderboard.value = []; // Set empty array on error
  }
};

const rewardSummary = ref(null);

const fetchRewardsSummary = async (id) => {
  try {
    if (!id) return;
    const data = await getRewardsByStudentId(id);
    rewardSummary.value = data ?? null;
  } catch (error) {
    console.error("Error fetching rewards:", error);
    rewardSummary.value = null;
  }
};

const availableXp = computed(() => rewardSummary.value?.availableXp ?? 0);

const sortedRewards = computed(() => {
  const rewards = rewardSummary.value?.rewards;
  if (!Array.isArray(rewards)) return [];
  return [...rewards]
    .filter((reward) => typeof reward?.costXp === "number" && reward.costXp > 0)
    .sort((a, b) => a.costXp - b.costXp);
});

const nextRewardTarget = computed(() => {
  const rewards = sortedRewards.value;
  const available = availableXp.value;
  if (!rewards.length) return null;
  const next = rewards.find((reward) => reward.costXp > available);
  return next ?? rewards[rewards.length - 1];
});

const xpTarget = computed(() => {
  const target = nextRewardTarget.value?.costXp;
  if (typeof target === "number" && target > 0) return target;
  return 500;
});

const xpProgressPercentage = computed(() => {
  const target = xpTarget.value;
  if (!target) return 0;
  const percentage = (availableXp.value / target) * 100;
  return Number.isFinite(percentage)
    ? Math.min(Math.max(percentage, 0), 100)
    : 0;
});

const xpProgressDisplay = computed(
  () => `${xpProgressPercentage.value.toFixed(2)}%`
);
function getBadge(rank) {
  if (rank === 1) return require("@/assets/Badge1.png");
  if (rank === 2) return require("@/assets/Badge2.png");
  if (rank === 3) return require("@/assets/Badge3.png");
  return require("@/assets/Badge3.png");
}

onMounted(() => {
  handleGetProfile();
  fetchLeaderboard();
});

const subjects = [
  { name: "Science", icon: require("@/assets/biology.png") },
  { name: "Sejarah", icon: require("@/assets/history.png") },
  { name: "Mathematics", icon: require("@/assets/geography.png") },
  { name: "Other", icon: require("@/assets/economics.png") },
];
</script>
